/**
 * Dashboard Selector Controller Module
 * Manages the CIO Command Center dashboard selector with two dashboard options
 *
 * Features:
 * - Large clickable cards for dashboard selection
 * - Modal-based dashboard display
 * - Shared Ask Ada component
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only dashboard selection logic
 * - Template Method: Uses BaseController hooks
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';
import { environment } from '../../config/environment';

/**
 * Dashboard type enumeration
 */
type DashboardType = 'health-risk' | 'platform-consolidation';

/**
 * DashboardSelectorController class
 * Manages the CIO Command Center dashboard selector
 */
export class DashboardSelectorController extends BaseController {
    private activeDashboard: DashboardType | null = null;
    private modalOpenedAt: number = 0;

    // Health & Risk Dashboard content (embedded) - HBR Executive Style
    private readonly healthRiskContent: string = `
<div class="h-full flex flex-col bg-gray-100">
    <!-- Three Column Layout -->
    <div class="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 overflow-auto">
        <!-- Left: Health Status -->
        <div class="bg-white rounded-lg shadow-sm p-6">
            <div class="flex flex-col items-center mb-6">
                <div class="w-36 h-36 rounded-full bg-green-500 flex items-center justify-center shadow-lg">
                    <svg class="w-20 h-20 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                        <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="mt-4 text-center">
                    <span class="text-gray-700 text-lg">Overall IT Health: </span>
                    <span class="text-green-600 text-lg font-bold">GREEN</span>
                </div>
            </div>
            <div class="grid grid-cols-3 gap-2">
                <div class="text-center">
                    <svg viewBox="0 0 100 60" class="w-full h-14">
                        <path d="M 10,50 A 40,40 0 0,1 90,50" fill="none" stroke="#e5e7eb" stroke-width="8" stroke-linecap="round"/>
                        <path d="M 10,50 A 40,40 0 0,1 88,48" fill="none" stroke="#22c55e" stroke-width="8" stroke-linecap="round"/>
                        <line x1="50" y1="50" x2="85" y2="25" stroke="#374151" stroke-width="2" stroke-linecap="round"/>
                        <circle cx="50" cy="50" r="4" fill="#374151"/>
                    </svg>
                    <div class="text-xs text-gray-600">Service Availability</div>
                    <div class="flex items-center justify-center gap-1">
                        <span class="text-sm font-bold">99.9%</span>
                        <span class="w-2 h-2 rounded-full bg-green-500"></span>
                    </div>
                </div>
                <div class="text-center">
                    <svg viewBox="0 0 100 60" class="w-full h-14">
                        <path d="M 10,50 A 40,40 0 0,1 90,50" fill="none" stroke="#e5e7eb" stroke-width="8" stroke-linecap="round"/>
                        <path d="M 10,50 A 40,40 0 0,1 60,15" fill="none" stroke="#eab308" stroke-width="8" stroke-linecap="round"/>
                        <line x1="50" y1="50" x2="55" y2="20" stroke="#374151" stroke-width="2" stroke-linecap="round"/>
                        <circle cx="50" cy="50" r="4" fill="#374151"/>
                    </svg>
                    <div class="text-xs text-gray-600">Security</div>
                    <div class="flex items-center justify-center gap-1">
                        <span class="text-sm font-bold">Posture</span>
                        <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
                    </div>
                </div>
                <div class="text-center">
                    <svg viewBox="0 0 100 60" class="w-full h-14">
                        <path d="M 10,50 A 40,40 0 0,1 90,50" fill="none" stroke="#e5e7eb" stroke-width="8" stroke-linecap="round"/>
                        <path d="M 10,50 A 40,40 0 0,1 55,12" fill="none" stroke="#eab308" stroke-width="8" stroke-linecap="round"/>
                        <line x1="50" y1="50" x2="50" y2="15" stroke="#374151" stroke-width="2" stroke-linecap="round"/>
                        <circle cx="50" cy="50" r="4" fill="#374151"/>
                    </svg>
                    <div class="text-xs text-gray-600">Security Posture</div>
                    <div class="flex items-center justify-center gap-1">
                        <span class="text-xs font-bold">Slow Arch Review</span>
                        <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Center: Identified Risks -->
        <div class="bg-white rounded-lg shadow-sm p-6">
            <h2 class="text-lg font-bold text-gray-800 text-center mb-4">Identified Risks</h2>
            <div class="space-y-3">
                <div class="bg-red-800 rounded-lg p-4 text-white flex items-center gap-3">
                    <svg class="w-8 h-8 text-yellow-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2L1 21h22L12 2zm0 4l7.53 13H4.47L12 6zm-1 5v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
                    </svg>
                    <div class="font-bold">Architectural Debt: Dual ITSM</div>
                </div>
                <div class="bg-red-800 rounded-lg p-4 text-white flex items-center gap-3">
                    <svg class="w-8 h-8 text-yellow-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2L1 21h22L12 2zm0 4l7.53 13H4.47L12 6zm-1 5v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
                    </svg>
                    <div>
                        <div class="font-bold">Vendor Lock-in</div>
                        <div class="text-sm opacity-90">Salesforce Cloud Costs</div>
                    </div>
                </div>
                <div class="bg-red-800 rounded-lg p-4 text-white flex items-center gap-3">
                    <svg class="w-8 h-8 text-yellow-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2L1 21h22L12 2zm0 4l7.53 13H4.47L12 6zm-1 5v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
                    </svg>
                    <div class="font-bold">Manual Processes</div>
                </div>
            </div>
        </div>

        <!-- Right: Recommended Actions -->
        <div class="bg-white rounded-lg shadow-sm p-6">
            <h2 class="text-lg font-bold text-gray-800 text-center mb-1">Recommended Actions</h2>
            <p class="text-sm text-gray-500 text-center mb-4">(12-Week Outlook)</p>
            <div class="space-y-3">
                <div class="bg-green-600 rounded-lg p-4 text-white flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </svg>
                    </div>
                    <div>
                        <div class="font-bold">Sunset BMC & Migrate to</div>
                        <div class="font-bold">SNOW ITOM</div>
                    </div>
                </div>
                <div class="bg-green-600 rounded-lg p-4 text-white flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                        </svg>
                    </div>
                    <div>
                        <div class="font-bold">Optimize Salesforce</div>
                        <div class="font-bold">Licenses</div>
                    </div>
                </div>
                <div class="bg-green-600 rounded-lg p-4 text-white flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                        </svg>
                    </div>
                    <div>
                        <div class="font-bold">Implement Automated</div>
                        <div class="font-bold">Discovery</div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Footer -->
    <div class="p-4 text-center bg-gray-100">
        <div class="text-lg font-bold text-gray-800">DECISION CONFIDENCE:</div>
        <div class="text-sm text-gray-600">DATA-DRIVEN. ARCHITECTURE-ALIGNED. EXECUTION-READY.</div>
    </div>
</div>`;

    // Platform Consolidation Dashboard content (embedded) - HBR Executive Style
    private readonly platformConsolidationContent: string = `
<div class="h-full flex flex-col bg-gray-100">
    <!-- Column Headers -->
    <div class="grid grid-cols-12 gap-3 px-3 mb-2">
        <div class="col-span-4 text-center text-sm text-gray-500 uppercase tracking-wide font-medium">Current Enterprise Service Map</div>
        <div class="col-span-3 text-center text-sm text-gray-500 uppercase tracking-wide font-medium">Scenario Builder</div>
        <div class="col-span-5 text-center text-sm text-gray-500 uppercase tracking-wide font-medium">Projected Scenario Impact (12-Week View)</div>
    </div>

    <!-- Three Column Layout - adjusted proportions: 4/3/5 -->
    <div class="flex-1 grid grid-cols-12 gap-3 px-3 pb-3 overflow-auto">
        <!-- Left: Service Map Visualization - NARROWER (4 cols), less padding -->
        <div class="col-span-4 bg-white rounded-lg shadow-sm p-2 relative min-h-[420px]">
            <svg viewBox="0 0 260 380" class="w-full h-full" preserveAspectRatio="xMidYMid meet">
                <!-- Connection lines (arrows) -->
                <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#9ca3af"/>
                    </marker>
                </defs>
                <!-- Lines from Salesforce to ServiceNow -->
                <line x1="85" y1="110" x2="135" y2="180" stroke="#9ca3af" stroke-width="2.5" marker-end="url(#arrowhead)"/>
                <line x1="95" y1="105" x2="165" y2="175" stroke="#9ca3af" stroke-width="2.5" marker-end="url(#arrowhead)"/>
                <!-- Lines from Salesforce to BMC -->
                <line x1="65" y1="130" x2="65" y2="265" stroke="#9ca3af" stroke-width="2.5" marker-end="url(#arrowhead)"/>
                <line x1="85" y1="125" x2="85" y2="260" stroke="#9ca3af" stroke-width="2.5" marker-end="url(#arrowhead)"/>
                <!-- Lines from ServiceNow to BMC -->
                <line x1="135" y1="235" x2="95" y2="275" stroke="#9ca3af" stroke-width="2.5" marker-end="url(#arrowhead)"/>
                <line x1="165" y1="240" x2="110" y2="280" stroke="#9ca3af" stroke-width="2.5" marker-end="url(#arrowhead)"/>
                <!-- Cross lines -->
                <line x1="105" y1="100" x2="125" y2="185" stroke="#9ca3af" stroke-width="2.5" marker-end="url(#arrowhead)"/>
                <line x1="45" y1="120" x2="45" y2="270" stroke="#9ca3af" stroke-width="2.5" marker-end="url(#arrowhead)"/>
                <!-- Salesforce Circle (Green) - Centered better -->
                <circle cx="75" cy="80" r="60" fill="#22c55e"/>
                <text x="75" y="68" text-anchor="middle" fill="white" font-weight="bold" font-size="16">Salesforce</text>
                <text x="75" y="86" text-anchor="middle" fill="white" font-size="11">(Customer</text>
                <text x="75" y="100" text-anchor="middle" fill="white" font-size="11">Workflows)</text>
                <!-- ServiceNow Circle (Blue) -->
                <circle cx="165" cy="205" r="60" fill="#3b82f6"/>
                <text x="165" y="193" text-anchor="middle" fill="white" font-weight="bold" font-size="16">ServiceNow</text>
                <text x="165" y="211" text-anchor="middle" fill="white" font-size="11">(ITSM</text>
                <text x="165" y="225" text-anchor="middle" fill="white" font-size="11">Processes)</text>
                <!-- BMC Circle (Gray) -->
                <circle cx="75" cy="310" r="50" fill="#6b7280"/>
                <text x="75" y="302" text-anchor="middle" fill="white" font-weight="bold" font-size="14">BMC</text>
                <text x="75" y="318" text-anchor="middle" fill="white" font-size="10">(Legacy Ops</text>
                <text x="75" y="330" text-anchor="middle" fill="white" font-size="10">Tooling)</text>
            </svg>
            <!-- Flexera Alert - Compact -->
            <div class="absolute top-2 right-2">
                <div class="flex items-start gap-1 bg-red-50 border border-red-200 rounded-lg p-2">
                    <svg class="w-5 h-5 text-red-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2L1 21h22L12 2zm0 4l7.53 13H4.47L12 6zm-1 5v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
                    </svg>
                    <div>
                        <div class="font-bold text-red-700 text-xs">Flexera:</div>
                        <div class="text-red-600 text-xs">Overlapping Licenses</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Center: Scenario Builder - same (3 cols) -->
        <div class="col-span-3 bg-white rounded-lg shadow-sm p-3">
            <div class="space-y-4">
                <!-- Toggle 1: Consolidate on ServiceNow -->
                <div>
                    <div class="font-bold text-gray-800 text-center text-sm mb-2">CONSOLIDATE ON<br/>SERVICENOW</div>
                    <div class="flex justify-center items-center gap-2">
                        <label class="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" id="toggle-consolidate-servicenow" class="sr-only peer" checked>
                            <div class="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[3px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                        <span class="text-sm font-bold text-blue-600" id="label-consolidate">ON</span>
                    </div>
                </div>
                <!-- Toggle 2: Decommission BMC -->
                <div>
                    <div class="font-bold text-gray-800 text-center text-sm mb-2">Decommission BMC</div>
                    <div class="flex justify-center items-center gap-2">
                        <label class="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" id="toggle-decommission-bmc" class="sr-only peer">
                            <div class="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[3px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                        <span class="text-sm font-bold text-gray-500" id="label-decommission">OFF</span>
                    </div>
                </div>
                <!-- Toggle 3: Simplify Salesforce -->
                <div>
                    <div class="font-bold text-gray-800 text-center text-sm mb-2">Simplify Salesforce<br/>to "Engagement"</div>
                    <div class="flex justify-center items-center gap-2">
                        <label class="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" id="toggle-simplify-salesforce" class="sr-only peer" checked>
                            <div class="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[3px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                        <span class="text-sm font-bold text-blue-600" id="label-simplify">ON</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right: Projected Impact - WIDER with BIGGER metrics (5 cols) -->
        <div class="col-span-5 bg-white rounded-lg shadow-sm p-4">
            <!-- Three Gauges - MUCH BIGGER -->
            <div class="grid grid-cols-3 gap-4">
                <!-- Savings Gauge -->
                <div class="text-center">
                    <div class="font-bold text-gray-700 text-base mb-1">Projected Annual<br/>Savings</div>
                    <div class="text-xs text-gray-500 mb-1">(Flexera Data)</div>
                    <svg viewBox="0 0 100 80" class="w-full h-28">
                        <path d="M 15,65 A 35,35 0 0,1 85,65" fill="none" stroke="#e5e7eb" stroke-width="14" stroke-linecap="round"/>
                        <path d="M 15,65 A 35,35 0 0,1 75,35" fill="none" stroke="#22c55e" stroke-width="14" stroke-linecap="round"/>
                        <polygon points="50,12 36,35 43,35 43,58 57,58 57,35 64,35" fill="#22c55e"/>
                    </svg>
                    <div class="text-4xl font-bold text-blue-600 mt-1" id="savings-value">$4.2M</div>
                    <div class="text-base text-gray-600 mt-1">Projected Annual</div>
                    <div class="text-base text-gray-600">Savings</div>
                </div>
                <!-- Blast Radius Heatmap -->
                <div class="text-center">
                    <div class="font-bold text-gray-700 text-base mb-1">Operational<br/>Blast Radius</div>
                    <div class="relative mx-auto w-28 h-28 mt-1">
                        <svg viewBox="0 0 100 100" class="w-full h-full">
                            <defs>
                                <linearGradient id="heatmap" x1="0%" y1="100%" x2="100%" y2="0%">
                                    <stop offset="0%" style="stop-color:#22c55e"/>
                                    <stop offset="33%" style="stop-color:#84cc16"/>
                                    <stop offset="50%" style="stop-color:#eab308"/>
                                    <stop offset="75%" style="stop-color:#f97316"/>
                                    <stop offset="100%" style="stop-color:#ef4444"/>
                                </linearGradient>
                            </defs>
                            <rect x="5" y="5" width="90" height="90" fill="url(#heatmap)" rx="6"/>
                            <rect x="12" y="55" width="32" height="32" fill="white" stroke="#22c55e" stroke-width="5" rx="3"/>
                        </svg>
                    </div>
                    <div class="text-xl font-bold text-orange-500 mt-1" id="blast-value">Reduced</div>
                    <div class="text-lg text-gray-700 font-semibold" id="blast-percent">by 30%</div>
                    <div class="text-base text-gray-600 mt-1">Operational</div>
                    <div class="text-base text-gray-600">Blast Radius</div>
                </div>
                <!-- Complexity Speedometer -->
                <div class="text-center">
                    <div class="font-bold text-gray-700 text-base mb-1">Delivery<br/>Complexity</div>
                    <svg viewBox="0 0 100 70" class="w-full h-28 mt-1">
                        <path d="M 10,55 A 40,40 0 0,1 90,55" fill="none" stroke="#e5e7eb" stroke-width="12" stroke-linecap="round"/>
                        <path d="M 10,55 A 40,40 0 0,1 25,25" fill="none" stroke="#ef4444" stroke-width="12" stroke-linecap="round"/>
                        <path d="M 25,25 A 40,40 0 0,1 50,15" fill="none" stroke="#eab308" stroke-width="12" stroke-linecap="round"/>
                        <path d="M 50,15 A 40,40 0 0,1 90,55" fill="none" stroke="#22c55e" stroke-width="12" stroke-linecap="round"/>
                        <line x1="50" y1="55" x2="78" y2="30" stroke="#374151" stroke-width="5" stroke-linecap="round"/>
                        <circle cx="50" cy="55" r="7" fill="#374151"/>
                        <polygon points="82,22 72,38 90,38" fill="#22c55e"/>
                    </svg>
                    <div class="text-xl font-bold text-green-600 mt-1" id="complexity-label">Simplified</div>
                    <div class="text-base text-gray-600 mt-1">Delivery</div>
                    <div class="text-base text-gray-600">Complexity</div>
                </div>
            </div>
        </div>
    </div>

    <!-- Decision Confidence Footer - BELOW columns -->
    <div class="px-4 pb-4">
        <div class="bg-white rounded-lg shadow-sm p-4 text-center">
            <div class="text-lg font-bold text-gray-800 mb-1">DECISION CONFIDENCE:</div>
            <div class="text-sm text-gray-600 tracking-wide">DATA-DRIVEN. ARCHITECTURE-ALIGNED. EXECUTION-READY.</div>
        </div>
    </div>
</div>`;

    constructor(services: ControllerServices) {
        super(services);
    }

    getPageName(): PageName {
        return 'dashboard';
    }

    protected async doInit(): Promise<void> {
        console.log('Dashboard Selector: Initializing...');
    }

    protected setupEventListeners(): void {
        // Health & Risk Dashboard Card - use mouseup to avoid event timing issues
        const healthRiskCard = document.getElementById('health-risk-card');
        if (healthRiskCard) {
            healthRiskCard.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                // Delay opening slightly to ensure click event is fully processed
                setTimeout(() => this.openDashboard('health-risk'), 10);
            });
        }

        // Platform Consolidation Dashboard Card
        const platformCard = document.getElementById('platform-consolidation-card');
        if (platformCard) {
            platformCard.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                // Delay opening slightly to ensure click event is fully processed
                setTimeout(() => this.openDashboard('platform-consolidation'), 10);
            });
        }

        // Modal controls
        this.setupModalControls();

        // Sprint 50: Health widget
        this.setupHealthWidget();

        console.log('Dashboard Selector: Event listeners attached');
    }

    /**
     * Setup modal controls
     */
    private setupModalControls(): void {
        const backdrop = document.getElementById('dashboard-modal-backdrop');
        const closeBtn = document.getElementById('dashboard-modal-close');
        const backBtn = document.getElementById('dashboard-modal-back');
        const modalContent = document.getElementById('dashboard-modal-content');

        // Use mousedown to track where click started
        let mouseDownOnBackdrop = false;

        // Close on backdrop click (only if BOTH mousedown and mouseup occur on backdrop)
        if (backdrop) {
            backdrop.addEventListener('mousedown', (e) => {
                if (e.target === backdrop) {
                    mouseDownOnBackdrop = true;
                    console.log('Dashboard Selector: Mousedown on backdrop');
                }
            });

            backdrop.addEventListener('mouseup', (e) => {
                if (e.target === backdrop && mouseDownOnBackdrop) {
                    console.log('Dashboard Selector: Mouseup on backdrop - closing');
                    this.closeDashboard();
                }
                mouseDownOnBackdrop = false;
            });

            // Reset if mouse leaves backdrop during click
            backdrop.addEventListener('mouseleave', () => {
                mouseDownOnBackdrop = false;
            });
        }

        // Prevent clicks inside modal content from triggering backdrop close
        if (modalContent) {
            modalContent.addEventListener('mousedown', (e) => {
                e.stopPropagation();
                mouseDownOnBackdrop = false;
            });
            modalContent.addEventListener('mouseup', (e) => {
                e.stopPropagation();
            });
            modalContent.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        }

        // Close on X button
        if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.closeDashboard();
            });
        }

        // Back button
        if (backBtn) {
            backBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.closeDashboard();
            });
        }

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.activeDashboard) {
                this.closeDashboard();
            }
        });
    }

    /**
     * Open a dashboard in the modal
     */
    private openDashboard(type: DashboardType): void {
        console.log(`Dashboard Selector: Opening ${type} dashboard...`);

        const modal = document.getElementById('dashboard-modal');
        const modalBody = document.getElementById('dashboard-modal-body');
        const modalTitle = document.getElementById('dashboard-modal-title');

        if (!modal || !modalBody || !modalTitle) {
            console.warn('Dashboard Selector: Modal elements not found', { modal, modalBody, modalTitle });
            return;
        }

        // Record when modal was opened (for close protection)
        this.modalOpenedAt = Date.now();

        // Set title and content
        if (type === 'health-risk') {
            modalTitle.textContent = 'Global IT Environment Health & Risk Status';
            modalBody.innerHTML = this.healthRiskContent;
        } else {
            modalTitle.textContent = 'CIO "If/What" Analysis: Platform Consolidation';
            modalBody.innerHTML = this.platformConsolidationContent;
        }

        // Show modal (just remove hidden class - CSS handles the rest)
        modal.classList.remove('hidden');

        // Initialize dashboard after content is injected
        if (type === 'health-risk') {
            this.initHealthRiskDashboard();
        } else {
            this.initPlatformConsolidationDashboard();
        }

        this.activeDashboard = type;
        console.log(`Dashboard Selector: Opened ${type} dashboard at ${this.modalOpenedAt}`);
    }

    /**
     * Close the dashboard modal
     */
    private closeDashboard(): void {
        const timeSinceOpen = Date.now() - this.modalOpenedAt;

        // Prevent closing if modal was opened less than 200ms ago (event bubbling protection)
        if (timeSinceOpen < 200) {
            console.log(`Dashboard Selector: Ignoring close - modal opened ${timeSinceOpen}ms ago`);
            return;
        }

        const modal = document.getElementById('dashboard-modal');

        if (modal) {
            modal.classList.add('hidden');
        }

        this.activeDashboard = null;
        this.modalOpenedAt = 0;
        console.log('Dashboard Selector: Closed dashboard');
    }

    /**
     * Initialize Health & Risk Dashboard after loading into modal
     */
    private initHealthRiskDashboard(): void {
        // Refresh button
        const refreshBtn = document.getElementById('health-refresh-btn');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => {
                console.log('Health Dashboard: Refreshing...');
                // Simulate refresh animation
                refreshBtn.classList.add('animate-spin');
                setTimeout(() => refreshBtn.classList.remove('animate-spin'), 1000);
            });
        }

        console.log('Dashboard Selector: Health & Risk Dashboard initialized');
    }

    /**
     * Initialize Platform Consolidation Dashboard after loading into modal
     */
    private initPlatformConsolidationDashboard(): void {
        // Initialize scenario toggles and service map
        this.initScenarioToggles();
        this.initServiceMapInModal();

        console.log('Dashboard Selector: Platform Consolidation Dashboard initialized');
    }

    /**
     * Initialize scenario toggles in the modal
     */
    private initScenarioToggles(): void {
        const toggles = [
            { id: 'toggle-consolidate-servicenow', labelId: 'label-consolidate' },
            { id: 'toggle-decommission-bmc', labelId: 'label-decommission' },
            { id: 'toggle-simplify-salesforce', labelId: 'label-simplify' }
        ];

        toggles.forEach(({ id, labelId }) => {
            const toggle = document.getElementById(id) as HTMLInputElement;
            const label = document.getElementById(labelId);
            if (toggle && label) {
                toggle.addEventListener('change', () => {
                    label.textContent = toggle.checked ? 'ON' : 'OFF';
                    this.updateActiveScenarios();
                    this.updateImpactMetrics();
                });
            }
        });
    }

    /**
     * Update active scenarios display
     */
    private updateActiveScenarios(): void {
        const container = document.getElementById('active-scenarios');
        if (!container) return;

        const badges: string[] = [];

        const consolidate = document.getElementById('toggle-consolidate-servicenow') as HTMLInputElement;
        const decommission = document.getElementById('toggle-decommission-bmc') as HTMLInputElement;
        const simplify = document.getElementById('toggle-simplify-salesforce') as HTMLInputElement;

        if (consolidate?.checked) {
            badges.push('<span class="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">Consolidate ServiceNow</span>');
        }
        if (decommission?.checked) {
            badges.push('<span class="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">Decommission BMC</span>');
        }
        if (simplify?.checked) {
            badges.push('<span class="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Simplify Salesforce</span>');
        }

        container.innerHTML = badges.length > 0
            ? badges.join('')
            : '<span class="text-gray-400 text-xs">No scenarios selected</span>';
    }

    /**
     * Update impact metrics based on scenarios
     */
    private updateImpactMetrics(): void {
        const consolidate = (document.getElementById('toggle-consolidate-servicenow') as HTMLInputElement)?.checked || false;
        const decommission = (document.getElementById('toggle-decommission-bmc') as HTMLInputElement)?.checked || false;
        const simplify = (document.getElementById('toggle-simplify-salesforce') as HTMLInputElement)?.checked || false;

        let savings = 0;
        let blastReduction = 0;
        let complexity = 0;

        if (consolidate) { savings += 2500000; blastReduction += 20; complexity++; }
        if (decommission) { savings += 1200000; blastReduction += 10; complexity++; }
        if (simplify) { savings += 500000; complexity++; }

        // Update savings display
        const savingsEl = document.getElementById('savings-value');
        if (savingsEl) {
            savingsEl.textContent = savings >= 1000000
                ? `$${(savings / 1000000).toFixed(1)}M`
                : `$${(savings / 1000).toFixed(0)}K`;
        }

        // Update blast radius
        const blastValueEl = document.getElementById('blast-value');
        const blastPercentEl = document.getElementById('blast-percent');
        if (blastValueEl && blastPercentEl) {
            blastValueEl.textContent = blastReduction > 0 ? 'Reduced' : 'Unchanged';
            blastValueEl.className = `text-sm font-bold ${blastReduction > 0 ? 'text-green-600' : 'text-gray-600'}`;
            blastPercentEl.textContent = blastReduction > 0 ? `by ${blastReduction}%` : '';
        }

        // Update complexity
        const complexityLabel = document.getElementById('complexity-label');
        if (complexityLabel) {
            let label = 'High';
            let color = 'text-red-600';
            if (complexity >= 3) { label = 'Simplified'; color = 'text-green-600'; }
            else if (complexity >= 2) { label = 'Low'; color = 'text-green-600'; }
            else if (complexity >= 1) { label = 'Medium'; color = 'text-yellow-600'; }
            complexityLabel.textContent = label;
            complexityLabel.className = `text-sm font-bold ${color}`;
        }
    }

    /**
     * Initialize service map in modal (static fallback)
     */
    private initServiceMapInModal(): void {
        const container = document.getElementById('cio-service-map');
        if (!container) return;

        // Use static SVG for modal display
        container.innerHTML = `
            <svg viewBox="0 0 300 250" class="w-full h-full">
                <g id="modal-salesforce" class="cursor-pointer" style="cursor: pointer;">
                    <circle cx="75" cy="60" r="40" fill="#22c55e" class="drop-shadow hover:opacity-80"/>
                    <text x="75" y="55" text-anchor="middle" fill="white" font-size="11" font-weight="bold" pointer-events="none">Salesforce</text>
                    <text x="75" y="70" text-anchor="middle" fill="white" font-size="8" pointer-events="none">(Customer</text>
                    <text x="75" y="80" text-anchor="middle" fill="white" font-size="8" pointer-events="none">Workflows)</text>
                </g>
                <g id="modal-servicenow" class="cursor-pointer" style="cursor: pointer;">
                    <circle cx="150" cy="170" r="45" fill="#3b82f6" class="drop-shadow hover:opacity-80"/>
                    <text x="150" y="165" text-anchor="middle" fill="white" font-size="11" font-weight="bold" pointer-events="none">ServiceNow</text>
                    <text x="150" y="180" text-anchor="middle" fill="white" font-size="8" pointer-events="none">(ITSM</text>
                    <text x="150" y="190" text-anchor="middle" fill="white" font-size="8" pointer-events="none">Processes)</text>
                </g>
                <g id="modal-bmc" class="cursor-pointer" style="cursor: pointer;">
                    <circle cx="225" cy="60" r="35" fill="#6b7280" class="drop-shadow hover:opacity-80"/>
                    <text x="225" y="55" text-anchor="middle" fill="white" font-size="11" font-weight="bold" pointer-events="none">BMC</text>
                    <text x="225" y="70" text-anchor="middle" fill="white" font-size="8" pointer-events="none">(Legacy Ops</text>
                    <text x="225" y="80" text-anchor="middle" fill="white" font-size="8" pointer-events="none">Tooling)</text>
                </g>
                <line x1="110" y1="80" x2="120" y2="130" stroke="#94a3b8" stroke-width="2" marker-end="url(#modal-arrow)"/>
                <line x1="190" y1="80" x2="180" y2="130" stroke="#94a3b8" stroke-width="2" marker-end="url(#modal-arrow)"/>
                <line x1="115" y1="60" x2="185" y2="60" stroke="#94a3b8" stroke-width="2"/>
                <defs>
                    <marker id="modal-arrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
                        <path d="M0,0 L0,6 L9,3 z" fill="#94a3b8"/>
                    </marker>
                </defs>
            </svg>
        `;
    }

    // =========================================================================
    // Sprint 50: System Health Widget
    // =========================================================================

    private healthRefreshInterval: ReturnType<typeof setInterval> | null = null;

    private setupHealthWidget(): void {
        const refreshBtn = document.getElementById('btn-health-refresh');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => this.fetchHealthScan());
        }
        // Initial fetch and auto-refresh every 60s
        this.fetchHealthScan();
        this.healthRefreshInterval = setInterval(() => this.fetchHealthScan(), 60000);
    }

    private async fetchHealthScan(): Promise<void> {
        const overallEl = document.getElementById('health-overall');
        const componentsEl = document.getElementById('health-components');
        const dataSummaryEl = document.getElementById('health-data-summary');

        if (overallEl) {
            overallEl.textContent = 'Scanning...';
            overallEl.className = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500';
        }

        try {
            const resp = await fetch(`${environment.proxyUrl}/health/scan`);
            const data = await resp.json();

            // Overall status badge
            if (overallEl) {
                const status = data.overall_status || 'unknown';
                const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
                    healthy: { bg: 'bg-green-100', text: 'text-green-800', label: 'HEALTHY' },
                    degraded: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'DEGRADED' },
                    critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'CRITICAL' },
                };
                const cfg = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-600', label: status.toUpperCase() };
                overallEl.textContent = cfg.label;
                overallEl.className = `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`;
            }

            // Component pills
            if (componentsEl) {
                const canisters = data.canisters || [];
                const services = data.services || [];
                const pills = [...canisters, ...services].map((c: any) => {
                    const s = (c.status || 'unknown').toLowerCase();
                    let colour = 'bg-gray-200 text-gray-600';
                    if (s === 'running' || s === 'ok') colour = 'bg-green-100 text-green-700';
                    else if (s === 'stopped' || s === 'error' || s === 'unreachable') colour = 'bg-red-100 text-red-700';
                    else if (s === 'timeout') colour = 'bg-yellow-100 text-yellow-700';
                    const latency = c.latency_ms ? ` (${c.latency_ms}ms)` : '';
                    return `<span class="inline-block px-2 py-0.5 rounded text-xs ${colour}" title="${c.name}${latency}">${c.name}</span>`;
                });
                componentsEl.innerHTML = pills.join('');
            }

            // Data summary
            if (dataSummaryEl) {
                const di = data.data_integrity || {};
                const parts: string[] = [];
                if (di.total_devices !== undefined) parts.push(`${di.total_devices} devices`);
                if (di.total_documents !== undefined) parts.push(`${di.total_documents} documents`);
                if (di.total_ea_entities !== undefined) parts.push(`${di.total_ea_entities} EA entities`);
                dataSummaryEl.textContent = parts.length ? parts.join(' | ') : '';
            }
        } catch (error) {
            if (overallEl) {
                overallEl.textContent = 'OFFLINE';
                overallEl.className = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
            }
            if (componentsEl) {
                componentsEl.innerHTML = '<span class="text-xs text-red-400">Cannot reach proxy</span>';
            }
        }
    }
}
